import { API_HEAD, BASE_URL } from '@/lib/apiConfig';

export interface Policy {
  id: number;
  type: 'terms' | 'privacy';
  fileName: string;
  filePath: string;
  fileUrl?: string;
  fileSize?: number;
  version?: string;
  isActive: boolean;
  uploadedBy?: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
  uploader?: {
    id: number;
    fullName: string;
    email: string;
  };
}

export interface PolicyListParams {
  page?: number;
  limit?: number;
  type?: 'terms' | 'privacy';
  includeInactive?: boolean;
}

export interface PolicyListResponse {
  success: boolean;
  data: Policy[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PolicyResponse {
  success: boolean;
  data: Policy;
  message?: string;
}

export interface UploadPolicyData {
  type: 'terms' | 'privacy';
  file: File;
  description?: string;
  version?: string;
}

type PublicRequestOptions = RequestInit & {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

class PolicyService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_HEAD;
  }

  private buildUrl(endpoint: string): string {
    const base = this.baseUrl.replace(/\/+$/, '');
    const path = endpoint.replace(/^\/+/, '');
    return `${base}/${path}`;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { makeAuthenticatedRequest } = await import('./apiHelper');

    const url = this.buildUrl(endpoint);
    const baseUrl = BASE_URL;

    try {
      const response = await makeAuthenticatedRequest<T>(url, options, baseUrl);
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        if (response.status === 401) {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('adminToken');
          }
          throw new Error(errorData.message || 'Không có token xác thực. Vui lòng đăng nhập lại.');
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Lỗi không xác định');
    }
  }

  private async publicRequest<T>(
    endpoint: string,
    options: PublicRequestOptions = {},
    silent404: boolean = false
  ): Promise<T> {
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(this.buildUrl(endpoint), config);

    if (!response.ok) {
      // Nếu là 404 và silent404 = true, không throw error (để xử lý im lặng)
      if (response.status === 404 && silent404) {
        const errorData = await response.json().catch(() => ({}));
        throw { status: 404, message: errorData.message || 'Not found', silent: true };
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Lấy danh sách policies (admin)
  async getPolicies(params: PolicyListParams = {}): Promise<PolicyListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.request<PolicyListResponse>(`/policies?${queryParams.toString()}`);
  }

  // Lấy policy active hiện tại (public)
  async getActivePolicy(type: 'terms' | 'privacy', options: PublicRequestOptions = {}): Promise<PolicyResponse> {
    try {
      return await this.publicRequest<PolicyResponse>(`/policies/active/${type}`, options, true);
    } catch (error: any) {
      // Nếu backend trả 404 (không có policy), đây là trường hợp bình thường
      // Không log error vào console vì đây không phải là lỗi
      if (error?.status === 404 || error?.silent) {
        return {
          success: false,
          message:
            error?.message ||
            (type === 'privacy'
              ? 'Không tìm thấy chính sách bảo mật hiện tại'
              : 'Không tìm thấy điều khoản sử dụng hiện tại'),
          data: undefined,
        } as unknown as PolicyResponse;
      }
      // Các lỗi khác vẫn throw để xử lý
      throw error;
    }
  }

  // Upload policy mới
  async uploadPolicy(data: UploadPolicyData): Promise<PolicyResponse> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('type', data.type);
    if (data.description) {
      formData.append('description', data.description);
    }
    if (data.version) {
      formData.append('version', data.version);
    }

    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${this.baseUrl}/policies/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Cập nhật policy
  async updatePolicy(id: number, data: Partial<Policy>): Promise<PolicyResponse> {
    return this.request<PolicyResponse>(`/policies/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  // Xóa policy
  async deletePolicy(id: number): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/policies/${id}`, {
      method: 'DELETE',
    });
  }

  // Download policy file
  async downloadPolicy(id: number): Promise<Blob> {
    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${this.baseUrl}/policies/${id}/download`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  // Lấy danh sách policies trong thùng rác
  async getTrashPolicies(params: PolicyListParams = {}): Promise<PolicyListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.request<PolicyListResponse>(`/policies/trash?${queryParams.toString()}`);
  }

  // Khôi phục policy
  async restorePolicy(id: number): Promise<PolicyResponse> {
    return this.request<PolicyResponse>(`/policies/${id}/restore`, {
      method: 'PATCH',
    });
  }

  // Xóa vĩnh viễn policy
  async permanentDeletePolicy(id: number): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/policies/${id}/permanent`, {
      method: 'DELETE',
    });
  }
}

const policyService = new PolicyService();
export default policyService;

